import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cx from 'classnames';

import BottomSheet from '../BottomSheet';
import Icon from '../Icon';
import ModalAction from '../ModalAction';
import ChordBarsMixer from '../../ChordBarsTransport/ChordBarsMixer';
import IntensityMeter from '../../IntensityMeter';

import { ReactComponent as ChevronIcon } from '../../../icons/arrow-right-circle-solid.svg';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as SlidersIcon } from '../../../icons/sliders.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';

import { BAND_LAYERS } from '../../../utils/chordBand';
import { INTENSITY_LEVELS, NORMAL } from '../../../utils/chartIntensity';
import {
  BLOCK_CHORDS,
  fitsMeter,
  getPadSoundId,
  getRhythmPattern,
  getRhythmPatternsByPace,
  hasBandLayer,
  hasBassLine,
  toRhythmId,
} from '../../../utils/chordRhythmPatterns';
import { getBeatsPerBar } from '../../../utils/songTiming';

import './RhythmStylePicker.scss';
import i18n from '../../../i18n';

// What a style brings in besides the piano and drums, as the labels under its name. The id picks
// the label's color.
const getStyleLayers = (pattern) => [
  ...(hasBassLine(pattern) ? [{ id: 'bass', label: i18n.t('player.bass') }] : []),
  ...(getPadSoundId(pattern) ? [{ id: 'pad', label: i18n.t('player.pad') }] : []),
  ...BAND_LAYERS.filter(({ id }) => hasBandLayer(pattern, id)).map(({ id, name }) => ({
    id,
    label: i18n.t(`player.band.${id}`, { defaultValue: name }),
  })),
];

// The level a player waits for, where it only comes in above Normal: the pad, the electric and the brass
const getEntryLevel = (layerId) => {
  const level = INTENSITY_LEVELS.find((step) => step[layerId] > 0)?.id;

  return level > NORMAL ? level : null;
};

// A meter as it's written, from its beats to the bar: six is 6/8
const toMeterLabel = (beats) => (beats === 6 ? '6/8' : `${beats}/4`);

// Most styles are named in Spanish already; the few that aren't are translated by id
const styleName = ({ id, name }) => i18n.t(`rhythmStyles.${id}`, { defaultValue: name });

/**
 * How a chart is played: block chords, or one of the rhythm styles. A row naming the current one,
 * opening a sheet of them all grouped by pace.
 *
 * Styles are picked by ear, so the sheet stays open between taps and carries its own Play when
 * given a player: it covers the dialog's. So does its Volume, which drops the levels over the list.
 *
 * Styles that aren't written for the chart's meter are greyed and say which meters they are; see
 * `meters` in chordRhythmPatterns.js. The rest are labelled with the players they bring in besides the
 * piano and drums, and a player that waits for a louder part carries the level it comes in at.
 *
 * @param {{choice: string, onChoose: function, timeSignature: string}} rhythm
 *   `choice` is BLOCK_CHORDS or a style id. A style held back by the meter reads as block chords
 *   here while it stays the choice.
 * @param {boolean} [mixed] - Reads Mixed, for several parts set to different styles
 * @param {string} [defaultChoice] - What plays with nothing picked, marked in the sheet so there is
 *   somewhere obvious to come back to
 * @param {object} [player] - From useChordBarsPreview, for the sheet's Play
 * @param {boolean} disabled - Greys the row, e.g. while the dialog's Play is sounding. The sheet
 *   still switches styles under its own Play, since that is how they are picked by ear.
 * @param {function} [onOpenChange] - Told when the sheet opens and closes, for a player that should
 *   only wake while it is up
 */
const RhythmStylePicker = ({ rhythm, mixed = false, defaultChoice, player, disabled = false, onOpenChange }) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const [isMixerOpen, setIsMixerOpen] = useState(false);
  const setIsOpen = (open) => {
    setOpen(open);
    if (!open) setIsMixerOpen(false);
    onOpenChange?.(open);
  };
  const { choice, onChoose, timeSignature } = rhythm;
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const groups = getRhythmPatternsByPace();

  // Resolved rather than read off the id, so a style stored by an older build still names something
  const currentId = toRhythmId(choice, beatsPerBar);
  const current = currentId ? getRhythmPattern(currentId) : null;
  // Block chords where the default is a style this meter can't play
  const defaultId = defaultChoice ? toRhythmId(defaultChoice, beatsPerBar) : null;
  const isDefault = (id) => defaultChoice !== undefined && id === defaultId;
  const defaultTag = <span className="default-tag">{t('player.default')}</span>;

  const play = player && (
    <ModalAction
      icon={player.isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
      label={player.playLabel}
      active={player.isPlaying}
      busy={player.playBusy}
      progress={player.playProgress}
      disabled={!player.canPlay}
      onClick={player.isPlaying ? player.stop : player.play}
    />
  );

  // Beside the X, as in the chart dialog's header
  const volume = player?.setInstrumentVolume && (
    <ModalAction
      icon={<SlidersIcon />}
      label={t('player.volume')}
      active={isMixerOpen}
      onClick={() => setIsMixerOpen((open) => !open)}
    />
  );

  return (
    <>
      <button
        type="button"
        className={cx('RhythmStylePicker', current ? `pace--${current.pace}` : 'off')}
        disabled={disabled}
        onClick={() => setIsOpen(true)}
      >
        <span className="label">{t('player.rhythm')}</span>
        <span className="value">
          {mixed ? t('player.mixed') : current ? styleName(current) : t('player.blockChords')}
        </span>
        <Icon icon={<ChevronIcon />} className="solid chevron" />
      </button>

      <BottomSheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('player.rhythm')}
        className="RhythmSheet"
        action={play}
        endAction={volume}
        sheet={
          volume && (
            <ChordBarsMixer
              isOpen={isMixerOpen}
              onClose={() => setIsMixerOpen(false)}
              player={player}
              idPrefix="rhythm-sheet"
              drums
            />
          )
        }
      >
        <button
          type="button"
          className={cx('style', 'style--blocks', { on: !current, 'is-default': isDefault('') })}
          aria-pressed={!current}
          onClick={() => onChoose(BLOCK_CHORDS)}
        >
          {t('player.blockChords')}
          {isDefault('') && defaultTag}
        </button>

        {groups.map(({ id: paceId, label, patterns }) => (
          <section key={paceId} className={cx('pace', `pace--${paceId}`)}>
            <h4 className="pace-title">{t(`player.pace.${paceId}`, { defaultValue: label })}</h4>

            <div className="styles">
              {patterns.map((pattern) => {
                const { id } = pattern;
                const on = current?.id === id;
                const fits = fitsMeter(pattern, beatsPerBar);
                const layers = getStyleLayers(pattern);

                return (
                  <button
                    key={id}
                    type="button"
                    className={cx('style', { on, 'is-default': isDefault(id) })}
                    aria-pressed={on}
                    disabled={!fits}
                    onClick={() => onChoose(id)}
                  >
                    <span className="name">{styleName(pattern)}</span>
                    {!fits && (
                      <span
                        className="meters"
                        aria-label={t('player.writtenFor', { meters: pattern.meters.map(toMeterLabel).join(', ') })}
                      >
                        {pattern.meters.map((beats) => (
                          <span key={beats} className="meter">
                            {toMeterLabel(beats)}
                          </span>
                        ))}
                      </span>
                    )}
                    {fits && layers.length > 0 && (
                      <span className="layers">
                        {layers.map(({ id: layerId, label }) => {
                          const entry = getEntryLevel(layerId);

                          return (
                            <span key={layerId} className={cx('layer', `layer--${layerId}`)}>
                              {label}
                              {entry && <IntensityMeter level={entry} className="layer-level" />}
                            </span>
                          );
                        })}
                      </span>
                    )}
                    {isDefault(id) && defaultTag}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </BottomSheet>
    </>
  );
};

RhythmStylePicker.propTypes = {
  rhythm: PropTypes.shape({
    choice: PropTypes.string.isRequired,
    onChoose: PropTypes.func.isRequired,
    timeSignature: PropTypes.string,
  }).isRequired,
  mixed: PropTypes.bool,
  defaultChoice: PropTypes.string,
  player: PropTypes.shape({
    isPlaying: PropTypes.bool,
    play: PropTypes.func,
    stop: PropTypes.func,
    canPlay: PropTypes.bool,
    playLabel: PropTypes.string,
    playBusy: PropTypes.bool,
    playProgress: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

export default RhythmStylePicker;
